import React from 'react';
import { Content, ContextualHelp, Flex, Heading } from '@adobe/react-spectrum';
import './cardStats.css';

/**
 * Section wrapper used by every stats surface (commander tabs + deck
 * panes + future adoption targets). Renders the seared `.statsSection`
 * block with an optional monospace title, italic subtitle, and a
 * ContextualHelp info affordance next to the title when help content is
 * supplied.
 *
 * Props:
 * - `title`       — uppercase monospace gold eyebrow at the top
 * - `subtitle`    — italic muted line below the title
 * - `helpHeading` — title of the ContextualHelp dialog (optional)
 * - `helpContent` — ReactNode rendered inside the help dialog (optional)
 * - `action`      — ReactNode docked to the right of the title row
 *                   (e.g. a picker, action button, or status badge)
 * - `children`    — section body
 * - `className`   — extra class on the `.statsSection` root
 */
export function StatsSection({
    title,
    subtitle,
    helpHeading,
    helpContent,
    action,
    children,
    className,
}) {
    const hasHeader = title || subtitle || action;
    return (
        <div className={`statsSection ${className || ''}`.trim()}>
            {hasHeader && (
                <div className="statsSectionHeader">
                    {(title || action) && (
                        <Flex direction="row" alignItems="center" columnGap={8}>
                            {title && <span className="statsSectionTitle">{title}</span>}
                            {helpHeading && helpContent && (
                                <ContextualHelp variant="help">
                                    <Heading>{helpHeading}</Heading>
                                    <Content>{helpContent}</Content>
                                </ContextualHelp>
                            )}
                            {action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
                        </Flex>
                    )}
                    {subtitle && <span className="statsSectionSubtitle">{subtitle}</span>}
                </div>
            )}
            {children}
        </div>
    );
}
